import React, { useState } from 'react';
import { 
  PROJECT_STATUS, 
  getProjectStatusConfig 
} from '../../../constants/projectConstants';

// Status options for admin dropdown
const ADMIN_STATUS_CHANGE_OPTIONS = [
  { 
    value: PROJECT_STATUS.APPROVED, 
    label: 'Approved', 
    color: 'bg-green-100 text-green-800'
  },
  { 
    value: PROJECT_STATUS.REJECTED, 
    label: 'Rejected', 
    color: 'bg-red-100 text-red-800'
  }
];

const StatusDropdown = ({ 
  project, 
  onStatusChange, 
  disabled = false,
  className = '',
  onApprovalRequest = null // New prop for triggering approval modal
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const currentStatusConfig = getProjectStatusConfig(project.status);

  // Only show dropdown for approved/rejected projects
  const showDropdown = project.status !== PROJECT_STATUS.PENDING;

  const handleStatusSelect = async (newStatus) => {
    if (newStatus === project.status) {
      setIsOpen(false);
      return;
    }

    // If changing to approved and we have an approval request handler, use it
    if (newStatus === PROJECT_STATUS.APPROVED && onApprovalRequest) {
      setIsOpen(false);
      onApprovalRequest(project);
      return;
    }

    try {
      setLoading(true);
      
      // For rejection or if no approval handler, use the original amount
      const approvedAmount = newStatus === PROJECT_STATUS.APPROVED 
        ? project.financialRequirement 
        : null;
        
      await onStatusChange(project.id, newStatus, approvedAmount);
      setIsOpen(false);
    } catch (error) {
      console.error('Error changing project status:', error);
    } finally {
      setLoading(false);
    }
  };

  // For pending projects, show static badge
  if (!showDropdown) {
    return (
      <span className={`
        inline-flex items-center rounded-full font-medium px-2.5 py-0.5 text-xs
        ${currentStatusConfig.color}
        ${className}
      `}>
        {currentStatusConfig.label}
      </span>
    );
  }

  return (
    <div className="relative">
      {/* Status Dropdown */}
      <div className="relative">
        <button
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`
            inline-flex items-center rounded-full font-medium px-2.5 py-0.5 text-xs
            ${currentStatusConfig.color}
            ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'}
            ${className}
          `}
        >
          {loading ? 'Updating...' : currentStatusConfig.label}
          {!disabled && !loading && (
            <svg 
              className="ml-1 w-3 h-3" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>

        {isOpen && !disabled && !loading && (
          <>
            <div className="absolute top-full left-0 mt-1 w-28 bg-surface-elevated border border-border-primary rounded-lg shadow-lg z-20">
              {ADMIN_STATUS_CHANGE_OPTIONS
                .filter(option => option.value !== project.status) // Don't show current status
                .map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleStatusSelect(option.value)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-surface-hover first:rounded-t-lg last:rounded-b-lg transition-colors"
                >
                  <span className={`
                    inline-flex items-center rounded-full font-medium px-2 py-0.5 text-xs
                    ${option.color}
                  `}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
            {/* Click outside to close */}
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default StatusDropdown;